import React, {useEffect, useState} from "react";
import {ResponseResultCode} from "../../../model/response-api";
import {parseNumberWithCommaDecimal} from "../../../utils/stringUtil";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import {DbUserService} from "../../../services/db/dbUserService";
import DashboardLoading from "./DashboardLoading";
import DashboardNoData from "./DashboardNoData";
import {DashboardModuleProps} from "../../../model/db/dashboardModuleProps";
import DashboardCard from "./DashboardCard";

const defaultChartSeries = [
  {
    name: '목표 RE(%)',
    data: [0, 0, 0],
    color: "#545CC1",
  },
  {
    name: '실적 RE(%)',
    data: [0, 0, 0],
    color: {
      linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
      stops: [
        [0, '#7EC7AF'],
        [1, '#68A4D4']
      ]
    },
  },
];

const options = {
  chart: {
    type: 'column',
    height: 277,
    spacingTop: 0,
    spacingBottom: 0,
  },
  title: { text: '' },
  exporting: { enabled: false },
  credits: { enabled: false },
  legend: {
    align: 'right',
    verticalAlign: 'top',
    layout: 'horizontal',
    symbolHeight: 10,
    padding: 10,
    itemStyle: {
      fontSize: '0.75rem',
      color: '#444444',
    },
  },
  plotOptions: {
    column: {
      grouping: true,
      borderWidth: 0,
      pointWidth: 20,
      borderRadiusTopLeft: '10%',
      borderRadiusTopRight: '10%'
    }
  },
  tooltip: {
    shared: true,
    formatter: function (this: Highcharts.TooltipFormatterContextObject) {
      let tooltip = `<span><strong style="font-size: 0.75rem;">${this.x}</strong></span><br/>`;
      this.points?.forEach((point: any) => {
        tooltip += `<span style="color:${point.color}">●&nbsp;</span>${point.series.name}: <strong style="font-size: 0.75rem;">${parseNumberWithCommaDecimal(point.y)}</strong><br/>`;
      });
      return tooltip;
    },
    style: {
      fontSize: '0.75rem',
    }
  },
  xAxis: {
    categories: [],
    title: '',
    min: 0,
    lineColor: '#999',
    lineWidth: 1,
    labels: {
      style: {
        color: '#444',
        fontSize: '0.7rem',
      },
    },
  },
  yAxis: [
    {
      title: {
        text: '(RE%)',
        align: 'high',
        rotation: 0,
        offset: 0,
        y: -25,
        style: {
          color: '#444',
          fontSize: '0.65rem',
        },
      },
      min: 0,
      softMax: 10,
      lineColor: '#999',
      lineWidth: 1,
      gridLineColor: '#CCC',
      gridLineWidth: 1,
      gridLineDashStyle: 'ShortDash',
      labels: {
        x: -5,
        y: 4,
        style: {
          color: '#444',
          fontSize: "0.7rem",
        },
        formatter: function (this: any) {
          return parseNumberWithCommaDecimal(this.value);
        },
      },
    },
  ],
  series: defaultChartSeries,
  responsive: {
    rules: [
      {
        condition: {
          maxWidth: 500,
        },
        chartOptions: {
          chart: {
            spacingTop: 20,
          },
          xAxis: {
            labels: {
              style: {
                fontSize: "0.5rem",
              },
            },
          },
          yAxis: [
            {
              title: {
                y: -10,
                style: {
                  fontSize: "0.5rem",
                },
              },
              labels: {
                style: {
                  fontSize: "0.5rem",
                },
              },
            },
          ],
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal',
            itemStyle: {
              fontSize: '0.6rem',
            },
          },
          tooltip: {
            style: {
              fontSize: '0.6rem',
            },
          },
        },
      },
    ],
  },
};

export default function ReGoalPfrc(props: Readonly<DashboardModuleProps>) {
  const [goalInfo, setGoalInfo] = useState<any>();
  const [chartOption, setChartOption] = useState<any>(options);
  const [isLoading, setIsLoading] = useState(true);
  const [existsGoalInfo, setExistsGoalInfo] = useState(false);

  useEffect(() => {
    getGoalInfo().finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    const currentYear = new Date().getFullYear(); // 현재 연도
    const recentYears = [
      (currentYear - 2).toString(),
      (currentYear - 1).toString(),
      currentYear.toString(),
    ];

    const series = defaultChartSeries;
    series[0].data = [0, 0, 0];
    series[1].data = [0, 0, 0];
    let categories = recentYears;

    if (goalInfo && goalInfo.length > 0) {
      series[0].data = goalInfo.map((item: any) => item.goalRt || 0);
      series[1].data = goalInfo.map((item: any) => item.rePercent || 0);
      categories = goalInfo.map((item: any) => item.plnYy);
      setExistsGoalInfo(true);
    }

    setChartOption((prevState: any) => ({
      ...prevState,
      xAxis: {
        ...prevState.xAxis,
        categories: categories,
      },
      series: series,
    }));
  }, [goalInfo]);

  async function getGoalInfo() {
    const response = await DbUserService.getInstance().getGoalInfo();

    if (response) {
      if (ResponseResultCode.Y === response.resultCode) {
        setGoalInfo(response.data);
      } else {
        setGoalInfo([]);
      }
    }
  }

  return (
    <DashboardCard
      title="RE 목표 대비 실적"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={true}
      moreButtonUrl="/rp/rdmpGoalSetting"
      dashboardConfig={props.dashboardConfig}
    >
      <DashboardLoading isLoading={isLoading} />
      {!isLoading && !existsGoalInfo ? (
        <DashboardNoData
          message="RE100 목표를 설정해 주세요."
          link={{text: '목표 설정하기', to: '/rp/rdmpGoalSetting'}}
          className="ex-data4"
        />
      ) : (
        <div className="inner-flex1">
          <div className="chart">
            <HighchartsReact highcharts={Highcharts} options={chartOption}/>
          </div>
        </div>
      )}
    </DashboardCard>
  );
}